<template>
  <div class="theme-page-container">
    <PremiumPage title="Theme Settings" subtitle="Customize the look and feel of your admin portal" showBack>
      <div class="pa-3 pa-md-6">
      <!-- ══════════════════════════════════════
             LIVE PREVIEW
        ══════════════════════════════════════ -->
      <div class="preview-section mb-4 mb-md-6">

        <!-- Section header -->
        <div class="preview-section-header">
          <div>
            <div class="preview-section-title">Live Preview</div>
            <div class="preview-section-sub">See how your colors look before saving</div>
          </div>
        </div>

        <!-- Preview shell: exact replica of your portal layout -->
        <div class="lp-shell-wrapper">
        <div class="lp-shell" :style="pv.shell">

          <!-- ── Sidebar ── -->
          <div class="lp-sidebar" :style="pv.sidebar">

            <!-- Logo bar -->
            <div class="lp-logo-bar" :style="`background:${pv.topbarBg}`">
              <div class="lp-logo-avatar">
                <template v-if="user && user.logo_url && !imageError">
                  <img :src="API_BASE_URL + '/uploads/' + user.logo_url" :alt="user.name"
                    style="width: 100%; height: 100%; border-radius: 50%; object-fit: contain;"
                    @error="imageError = true" />
                </template>
                <template v-else>
                  {{ user && user.name ? user.name.charAt(0).toUpperCase() : "S" }}
                </template>
              </div>
              <div>
                <div class="lp-logo-name">{{ user && user.name ? user.name : "Your Society" }}</div>
                <div class="lp-logo-sub">Society Management</div>
              </div>            </div>

            <!-- Nav items -->
            <div class="lp-nav-area">

              <div class="lp-nav-section-label" :style="pv.navLabel">OVERVIEW</div>
              <div class="lp-nav-row" :style="pv.navRow">
                <v-icon size="15" :color="pv.navIconColor">mdi-grid-large</v-icon>
                <span>Dashboard</span>
              </div>

              <div class="lp-nav-section-label" :style="{ ...pv.navLabel, marginTop: '8px' }">DAILY OPERATIONS</div>
              <div class="lp-nav-row" :style="pv.navRow">
                <v-icon size="15" :color="pv.navIconColor">mdi-account-arrow-right-outline</v-icon>
                <span>Visitors</span>
              </div>

              <div class="lp-nav-row" :style="pv.navRow">
                <v-icon size="15" :color="pv.navIconColor">mdi-face-agent</v-icon>
                <span>Help Desk</span>
              </div>

              <div class="lp-nav-section-label" :style="{ ...pv.navLabel, marginTop: '8px' }">MEMBERS</div>
              <div class="lp-nav-row" :style="pv.navRow">
                <v-icon size="15" :color="pv.navIconColor">mdi-account-clock-outline</v-icon>
                <span>Member Requests</span>
              </div>
              <div class="lp-nav-row lp-nav-active"
                :style="`background:${navActiveColor}22; color:${navActiveColor}; border-left:3px solid ${navActiveColor}`">
                <v-icon size="15" :color="navActiveColor">mdi-account-multiple-outline</v-icon>
                <span style="font-weight:600">Members</span>
              </div>
            </div>
          </div>

          <!-- ── Right side: topbar + content ── -->
          <div class="lp-right" :style="pv.right">

            <!-- Topbar -->
            <div class="lp-topbar" :style="`background:${pv.topbarBg}`">
              <div class="lp-topbar-title"></div>
              <div class="lp-topbar-actions">
                <div class="lp-topbar-btn"><v-icon size="15" color="white">mdi-chat-outline</v-icon></div>
                <div class="lp-topbar-btn" style="position:relative">
                  <v-icon size="15" color="white">mdi-bell-outline</v-icon>
                  <span class="lp-bell-dot"></span>
                </div>
                <div class="lp-topbar-avatar" :style="`background:${theme_button_color || buttonColor}`">
                  <template v-if="user && user.logo_url && !imageError">
                    <img :src="API_BASE_URL + '/uploads/' + user.logo_url" :alt="user.name"
                      style="width: 100%; height: 100%; border-radius: 50%; object-fit: contain;"
                      @error="imageError = true" />
                  </template>
                  <template v-else>
                    {{ user && user.name ? user.name.charAt(0).toUpperCase() : "S" }}
                  </template>
                </div>
              </div>
            </div>

            <!-- Page content -->
            <div class="lp-page-body">

              <!-- Page header row -->
              <div class="lp-page-header">
                <div>
                  <div class="lp-page-title" :style="pv.pageTitle">Members</div>
                  <div class="lp-page-sub" :style="pv.pageSub">Manage all resident members of the society</div>
                </div>
                <div class="lp-page-actions">
                  <div class="lp-search-box" :style="pv.searchBox">
                    <v-icon size="13" :color="pv.mutedColor">mdi-magnify</v-icon>
                    <span :style="pv.searchPlaceholder">Search...</span>
                  </div>
                  <div class="lp-filter-btn" :style="pv.filterBtn">
                    <v-icon size="12" :color="pv.filterIconColor">mdi-filter-outline</v-icon>
                    <span>Filter by Block</span>
                    <v-icon size="11" :color="pv.filterIconColor">mdi-chevron-down</v-icon>
                  </div>
                  <div class="lp-filter-btn" :style="pv.filterBtn">
                    <span>Unit</span>
                    <v-icon size="11" :color="pv.filterIconColor">mdi-chevron-down</v-icon>
                  </div>
                  <div class="lp-icon-btn" :style="`background:${pv.actionBtnBg}`">
                    <v-icon size="13" :color="pv.actionBtnIcon">mdi-download</v-icon>
                  </div>
                  <div class="lp-add-btn" :style="`background:${pv.actionBtnBg}; color:${pv.actionBtnIcon}`">
                    <v-icon size="12" :color="pv.actionBtnIcon">mdi-plus</v-icon>
                    <span>Add</span>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div class="lp-table-card" :style="pv.tableCard">
                <!-- Table header -->
                <div class="lp-table-head" :style="pv.tableHead">
                  <div class="lp-th lp-col-no" :style="{ ...pv.th, textAlign: 'center' }">No</div>
                  <div class="lp-th lp-col-name" :style="pv.th">Name</div>
                  <div class="lp-th lp-col-house" :style="pv.th">Unit</div>
                  <div class="lp-th lp-col-contact" :style="pv.th">Contact No</div>
                  <div class="lp-th lp-col-active" :style="pv.th">Last Active</div>
                  <div class="lp-th lp-col-actions" :style="{ ...pv.th, textAlign: 'right' }">Actions</div>
                </div>

                <!-- Skeleton rows -->
                <div v-for="i in 2" :key="i" class="lp-table-row" :style="pv.tableRow">
                  <div class="lp-td lp-col-no">
                    <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '22px', height: '18px', borderRadius: '5px' }"></div>
                  </div>
                  <div class="lp-td lp-col-name">
                    <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '26px', height: '26px', borderRadius: '50%', flexShrink: '0' }"></div>
                    <div class="lp-shimmer" :style="{ ...pv.shimmer, width: (i === 1 ? '80px' : '60px'), height: '12px', borderRadius: '4px' }"></div>
                  </div>
                  <div class="lp-td lp-col-house">
                    <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '52px', height: '20px', borderRadius: '6px' }"></div>
                  </div>
                  <div class="lp-td lp-col-contact">
                    <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '80px', height: '12px', borderRadius: '4px' }"></div>
                  </div>
                  <div class="lp-td lp-col-active">
                    <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '60px', height: '12px', borderRadius: '4px' }"></div>
                  </div>
                  <div class="lp-td lp-col-actions">
                    <div class="lp-action-group">
                      <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '22px', height: '22px', borderRadius: '6px' }"></div>
                      <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '22px', height: '22px', borderRadius: '6px' }"></div>
                      <div class="lp-shimmer" :style="{ ...pv.shimmer, width: '22px', height: '22px', borderRadius: '6px' }"></div>
                    </div>
                  </div>
                </div>

                <!-- Pagination row -->
                <div class="lp-pagination" :style="pv.pagination">
                  <div class="lp-show-rows" :style="pv.showRows">
                    Show rows
                    <span class="lp-rows-select" :style="pv.rowsSelect">10 <v-icon size="11" :color="pv.filterIconColor">mdi-chevron-down</v-icon></span>
                  </div>
                  <div class="lp-page-btns">
                    <span class="lp-page-info" :style="pv.pageInfo">1-4 of 4</span>
                    <div class="lp-page-btn" :style="pv.pageBtn"><v-icon size="12" :color="pv.mutedColor">mdi-chevron-double-left</v-icon></div>
                    <div class="lp-page-btn" :style="pv.pageBtn"><v-icon size="12" :color="pv.mutedColor">mdi-chevron-left</v-icon></div>
                    <div class="lp-page-btn" :style="`background:${pv.actionBtnBg}; border-color:transparent; color:${pv.actionBtnIcon}`">1</div>
                    <div class="lp-page-btn" :style="pv.pageBtn"><v-icon size="12" :color="pv.mutedColor">mdi-chevron-right</v-icon></div>
                    <div class="lp-page-btn" :style="pv.pageBtn"><v-icon size="12" :color="pv.mutedColor">mdi-chevron-double-right</v-icon></div>
                  </div>
                </div>
              </div>
              <!-- /table card -->

            </div>
            <!-- /lp-page-body -->

          </div>
          <!-- /lp-right -->

        </div>
        <!-- /lp-shell -->
        </div>
        <!-- /lp-shell-wrapper -->

      </div>
      <!-- /preview-section -->

      <!-- ══════════════════════════════════════
             APPEARANCE MODE
        ══════════════════════════════════════ -->
      <div class="appearance-section mb-4 mb-md-6">
        <div class="premium-panel">
          <div class="premium-panel-header">
            <div>
              <div class="premium-panel-title">Appearance</div>
              <div class="premium-panel-sub">
                Choose light, dark, or follow your system preference
              </div>
            </div>
          </div>
          <div class="premium-panel-body">
            <div class="appearance-options">
              <button
                v-for="option in appearanceOptions"
                :key="option.value"
                type="button"
                class="appearance-option"
                :class="{ 'appearance-option--active': themeMode === option.value }"
                @click="selectAppearance(option.value)"
              >
                <div class="appearance-option-icon">
                  <v-icon size="20">{{ option.icon }}</v-icon>
                </div>
                <div class="appearance-option-copy">
                  <div class="appearance-option-title">{{ option.label }}</div>
                  <div class="appearance-option-sub">{{ option.description }}</div>
                </div>
                <v-icon
                  v-if="themeMode === option.value"
                  size="16"
                  class="appearance-option-check"
                >
                  mdi-check-circle
                </v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <v-row :no-gutters="$vuetify.breakpoint.smAndDown">

        <!-- ── Primary Color Picker ── -->
        <v-col cols="12" md="6" class="mb-4 mb-md-0">
          <div class="premium-panel">
            <div class="premium-panel-header">
              <div>
                <div class="premium-panel-title">Primary Color</div>
                <div class="premium-panel-sub">Used for sidebar accent, active states, and highlights</div>
              </div>
              <div class="color-dot" :style="`background:${theme_primary_color || primaryColor}`"></div>
            </div>
            <div class="premium-panel-body">
              <v-color-picker v-model="theme_primary_color" dot-size="22" hide-canvas show-swatches
                swatches-max-height="180" mode="hexa" hide-mode-switch flat width="100%" />
            </div>
          </div>
        </v-col>

        <!-- ── Button Color Picker ── -->
        <v-col cols="12" md="6">
          <div class="premium-panel">
            <div class="premium-panel-header">
              <div>
                <div class="premium-panel-title">Button Color</div>
                <div class="premium-panel-sub">Used for action buttons and secondary elements</div>
              </div>
              <div class="color-dot" :style="`background:${theme_button_color || buttonColor}`"></div>
            </div>
            <div class="premium-panel-body">
              <v-color-picker v-model="theme_button_color" dot-size="22" hide-canvas show-swatches
                swatches-max-height="180" mode="hexa" hide-mode-switch flat width="100%" />
            </div>
          </div>
        </v-col>

      </v-row>

  </div>

  <template #footer>
    <v-btn text class="btn-premium-cancel" @click="resetColors">Reset to Default</v-btn>
    <v-btn depressed :loading="loading" class="btn-premium-primary ml-3" @click="updateTheme">Submit</v-btn>
  </template>
  </PremiumPage>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { THEME_MODES } from "../../store/theme";
import PremiumPage from "../common/PremiumPage.vue";

export default {
  components: { PremiumPage },

  data() {
    return {
      loading: false,
      theme_primary_color: "",
      theme_button_color: "",
      serverSideErrors: [],
      user: null,
      imageError: false,
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor", "themeMode"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL || "",

    // In dark mode the active nav item uses white (#E5E5E5) instead of the brand color
    navActiveColor() {
      return this.isDark ? '#E5E5E5' : (this.theme_primary_color || this.primaryColor);
    },

    // All dynamic inline styles for the Live Preview — driven by isDark.
    // Using inline styles instead of CSS classes avoids Vue scoped-style
    // specificity issues where the base class always wins.
    pv() {
      const d = this.isDark;
      // In dark mode the topbar/navbar uses #1e293b (same as table surface color),
      // the sidebar uses #1e293b — matching the table background for visual consistency.
      const topbarBg   = d ? '#1e293b' : (this.theme_primary_color || this.primaryColor);
      const sidebarBg  = d ? '#1e293b' : '#ffffff';
      const sidebarBdr = d ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb';
      return {
        shell: {
          background: d ? '#0f172a' : '#ffffff',
          border: d ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
        },
        sidebar: {
          background: sidebarBg,
          borderRight: sidebarBdr,
        },
        topbarBg,
        navLabel: {
          color: d ? '#4b5563' : '#9ca3af',
        },
        navRow: {
          color: d ? '#9ca3af' : '#4b5563',
        },
        navIconColor: d ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
        right: {
          background: d ? '#0f172a' : '#f8fafc',
        },
        pageTitle: {
          color: d ? '#f3f4f6' : '#111827',
        },
        pageSub: {
          color: '#6b7280',
        },
        searchBox: {
          background: d ? '#273548' : '#ffffff',
          border: d ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
        },
        searchPlaceholder: {
          color: d ? '#6b7280' : '#9ca3af',
          fontSize: '11px',
        },
        filterBtn: {
          background: d ? '#273548' : '#ffffff',
          border: d ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
          color: d ? '#d1d5db' : '#374151',
        },
        filterIconColor: d ? '#9ca3af' : '#374151',
        mutedColor: d ? '#6b7280' : '#9ca3af',
        tableCard: {
          background: d ? '#1e293b' : '#ffffff',
          border: d ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
        },
        tableHead: {
          background: d ? '#273548' : '#f9fafb',
          borderBottom: d ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
        },
        th: {
          color: d ? '#6b7280' : '#9ca3af',
        },
        tableRow: {
          borderBottom: d ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f3f4f6',
        },
        shimmer: d
          ? { background: 'linear-gradient(90deg,#273548 25%,#2f3f57 50%,#273548 75%)', backgroundSize: '200% 100%' }
          : { background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%' },
        pagination: {
          background: d ? '#273548' : '#fafafa',
          borderTop: d ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f3f4f6',
        },
        showRows: {
          color: d ? '#9ca3af' : '#6b7280',
        },
        rowsSelect: {
          border: d ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
          color: d ? '#d1d5db' : '#111827',
          background: d ? '#2f3f57' : 'transparent',
        },
        pageInfo: {
          color: d ? '#9ca3af' : '#6b7280',
        },
        pageBtn: {
          background: d ? '#273548' : '#ffffff',
          border: d ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
          color: d ? '#6b7280' : '#9ca3af',
        },
        // Action buttons (download / add) use dark-grey in dark mode, brand color in light
        actionBtnBg:   d ? '#2f3f57' : (this.theme_button_color || this.buttonColor),
        actionBtnIcon: d ? '#e5e5e5' : '#ffffff',
      };
    },

    appearanceOptions() {
      return [
        {
          value: THEME_MODES.LIGHT,
          label: "Light",
          icon: "mdi-white-balance-sunny",
          description: "Use your custom brand colors",
        },
        {
          value: THEME_MODES.DARK,
          label: "Dark",
          icon: "mdi-moon-waning-crescent",
          description: "Black-and-white high-contrast theme",
        },
        {
          value: THEME_MODES.SYSTEM,
          label: "System",
          icon: "mdi-monitor",
          description: "Match your device appearance",
        },
      ];
    },
  },

  methods: {
    ...mapActions("theme", ["setThemeMode", "applyAppearance"]),
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("theme", ["setPrimaryColor", "setButtonColor"]),

    selectAppearance(mode) {
      this.setThemeMode({ mode, vuetify: this.$vuetify });
    },

    cleanHex(val) {
      if (!val) return "";
      return val.replace(/FF$/i, "").toUpperCase();
    },

    resetColors() {
      this.theme_primary_color = "#f87676";
      this.theme_button_color = "#30384d";
    },

    updateTheme() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.loading = true;
        this.$axios
          .put("/societyadmin/theme", {
            theme_primary_color: this.theme_primary_color,
            theme_button_color: this.theme_button_color,
          })
          .then((response) => {
            const data = response.data;
            this.loading = false;
            this.serverSideErrors = {};

            if (data.status) {
              let user = JSON.parse(localStorage.getItem("user") || "{}");
              user.theme_primary_color = this.theme_primary_color;
              user.theme_button_color = this.theme_button_color;
              localStorage.setItem("user", JSON.stringify(user));

              this.setPrimaryColor({ primaryColor: this.theme_primary_color });
              this.setButtonColor({ buttonColor: this.theme_button_color });
              this.applyAppearance({ vuetify: this.$vuetify });

              this.setAlert({ showAlert: true, message: data.message, variant: "green accent-4", timeout: 2000 });
            } else {
              if (!data.errors) {
                this.setAlert({ showAlert: true, message: data.message, variant: "red accent-3", timeout: 2000 });
              } else {
                for (let i = 0; i < data.errors.length; i++) {
                  this.serverSideErrors[data.errors[i].field] = data.errors[i].message;
                }
              }
            }
          });
      });
    },
  },

  mounted() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    this.user = user;
    this.theme_primary_color = user.theme_primary_color || this.primaryColor || "#f87676";
    this.theme_button_color = user.theme_button_color || this.buttonColor || "#30384d";
  },
};
</script>

<style scoped>
/* ══════════════════════════════
   PAGE WRAPPER
══════════════════════════════ */
.theme-page-container {
  padding: 24px;
}

/* ══════════════════════════════
   COLOR PICKER PANELS
══════════════════════════════ */
.premium-panel {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.premium-panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.premium-panel-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--tx, #111827);
  letter-spacing: -0.01em;
}

.premium-panel-sub {
  font-size: 12px;
  color: var(--tx2, #6b7280);
  margin-top: 2px;
}

.premium-panel-body {
  padding: 20px;
  flex: 1;
}

.color-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  transition: background 0.25s ease;
}

.appearance-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 960px) {
  .appearance-options {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .appearance-options {
    grid-template-columns: 1fr;
  }
}

.appearance-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 12px;
  background: var(--surface2, #f9fafb);
  color: var(--tx, #111827);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

@media (max-width: 600px) {
  .appearance-option {
    padding: 12px 14px;
    gap: 10px;
  }
}

.appearance-option:hover {
  border-color: var(--brand, #f87676);
}

.appearance-option--active {
  border-color: var(--brand, #f87676);
  background: var(--brand-lt, rgba(248, 118, 118, 0.08));
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
}

.appearance-option-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--tx, #111827) !important;
}

.appearance-option-icon .v-icon {
  color: var(--tx, #111827) !important;
}


@media (max-width: 600px) {
  .appearance-option-icon {
    width: 32px;
    height: 32px;
  }
}

.appearance-option-copy {
  flex: 1;
  min-width: 0;
}

.appearance-option-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx, #111827);
}

.appearance-option-sub {
  margin-top: 2px;
  font-size: 12px;
  color: var(--tx2, #6b7280);
  line-height: 1.4;
}

@media (max-width: 600px) {
  .appearance-option-title {
    font-size: 13px;
  }

  .appearance-option-sub {
    font-size: 11px;
  }
}

.appearance-option-check {
  color: var(--brand, #f87676);
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .theme-page-container {
    padding: 16px;
  }

  /* Tighten panel body on mobile same as Appearance card */
  .premium-panel-header {
    padding: 12px 16px !important;
  }
  .premium-panel-body {
    padding: 12px !important;
  }
}

@media (max-width: 600px) {
  .theme-page-container {
    padding: 8px;
  }

  .premium-panel {
    border-radius: 10px;
  }

  .premium-panel-header {
    padding: 10px 12px !important;
  }
  .premium-panel-body {
    padding: 10px !important;
  }

  .premium-panel-title {
    font-size: 14px;
  }

  .premium-panel-sub {
    font-size: 11px;
  }

  .color-dot {
    width: 20px;
    height: 20px;
  }
}

.lp-shell-wrapper {
  overflow-x: auto;
  padding-bottom: 12px;
  scrollbar-width: none;
  margin-bottom: 8px;
}
.lp-shell-wrapper::-webkit-scrollbar {
  display: none;
}

@media (max-width: 600px) {
  .lp-shell-wrapper {
    padding-bottom: 8px;
    margin-bottom: 4px;
  }
}


/* v-color-picker overrides */
::v-deep .v-color-picker {
  box-shadow: none !important;
  background: transparent !important;
  width: 100% !important;
}

@media (max-width: 600px) {
  ::v-deep .v-color-picker {
    font-size: 12px !important;
  }
}

::v-deep .v-color-picker__sliders {
  padding: 0 0 8px 0 !important;
}

::v-deep .v-slider__track-container {
  height: 10px !important;
  border-radius: 8px !important;
  overflow: hidden;
}

::v-deep .v-slider__track-background,
::v-deep .v-slider__track-fill {
  border-radius: 8px !important;
}

::v-deep .v-slider__thumb {
  width: 18px !important;
  height: 18px !important;
  left: -9px !important;
  border: 2.5px solid #fff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25) !important;
  cursor: pointer;
}

@media (max-width: 600px) {
  ::v-deep .v-slider__thumb {
    width: 16px !important;
    height: 16px !important;
    left: -8px !important;
  }
}

::v-deep .v-slider__thumb::before {
  opacity: 0 !important;
}

::v-deep .v-color-picker__input input {
  border: 1.5px solid var(--border, #e2e8f0) !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  font-family: "Courier New", monospace !important;
  padding: 6px 10px !important;
  height: 36px !important;
  text-align: center;
  background: var(--surface2, #f9fafb) !important;
  color: var(--tx, #111827) !important;
  box-shadow: none !important;
  transition: border-color 0.2s;
}

@media (max-width: 600px) {
  ::v-deep .v-color-picker__input input {
    font-size: 12px !important;
    padding: 5px 8px !important;
    height: 34px !important;
  }
}

::v-deep .v-color-picker__input input:focus {
  border-color: var(--brand) !important;
  outline: none;
}

::v-deep .v-color-picker__input span {
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  color: var(--tx3, #9ca3af) !important;
  text-transform: uppercase;
  margin-top: 4px !important;
}

::v-deep .v-color-picker__color {
  border-radius: 4px !important;
  height: 18px !important;
  width: 26px !important;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

::v-deep .v-color-picker__color:hover {
  transform: scale(1.15) !important;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2) !important;
  z-index: 1;
  position: relative;
}

/* ══════════════════════════════
   PREVIEW SECTION HEADER
══════════════════════════════ */
.preview-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 600px) {
  .preview-section-header {
    margin-bottom: 12px;
  }
}

.preview-section-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: var(--tx, #111827);
  letter-spacing: -0.01em;
  margin-bottom: 2px;
}

.preview-section-sub {
  font-size: 12px;
  color: var(--tx2, #6b7280);
}

@media (max-width: 600px) {
  .preview-section-title {
    font-size: 15px;
  }

  .preview-section-sub {
    font-size: 11px;
  }
}

.preview-color-badges {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .preview-color-badges {
    gap: 8px;
  }
}

.preview-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 30px;
  padding: 5px 12px 5px 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

@media (max-width: 600px) {
  .preview-badge {
    padding: 4px 10px 4px 5px;
    gap: 5px;
  }
}

.preview-badge-swatch {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  transition: background 0.25s ease;
}

.preview-badge-label {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.preview-badge-hex {
  font-family: "Courier New", monospace;
  font-size: 11px;
  font-weight: 700;
  color: #111827;
}

@media (max-width: 600px) {
  .preview-badge-label {
    font-size: 9px;
  }

  .preview-badge-hex {
    font-size: 10px;
  }
}

/* ══════════════════════════════
   PREVIEW SHELL
══════════════════════════════ */
.lp-shell {
  display: flex;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  height: auto;
  background: #fff;
  min-width: 700px; /* Ensures it doesn't get squished */
}

@media (max-width: 600px) {
  .lp-shell {
    min-width: 600px;
    border-radius: 12px;
  }
}

/* ══════════════════════════════
   SIDEBAR  — matches your portal
══════════════════════════════ */
.lp-sidebar {
  width: 200px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (max-width: 600px) {
  .lp-sidebar {
    width: 170px;
  }
}

.lp-logo-bar {
  height: 56px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 14px;
  flex-shrink: 0;
  transition: background 0.3s ease;
}

@media (max-width: 600px) {
  .lp-logo-bar {
    height: 50px;
    padding: 0 10px;
    gap: 7px;
  }
}

.lp-logo-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13px;
  color: #333;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .lp-logo-avatar {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
}

.lp-logo-name {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  white-space: nowrap;
}

.lp-logo-sub {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.75);
}

@media (max-width: 600px) {
  .lp-logo-name {
    font-size: 11px;
  }

  .lp-logo-sub {
    font-size: 8px;
  }
}

.lp-nav-area {
  flex: 1;
  padding: 10px 0;
  overflow: hidden;
}

.lp-nav-section-label {
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ca3af;
  padding: 5px 14px 3px;
}

@media (max-width: 600px) {
  .lp-nav-section-label {
    font-size: 8px;
    padding: 4px 10px 2px;
  }
}

.lp-nav-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px 7px 17px;
  font-size: 11.5px;
  color: #4b5563;
  cursor: default;
  white-space: nowrap;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}

@media (max-width: 600px) {
  .lp-nav-row {
    font-size: 10.5px;
    padding: 6px 10px 6px 13px;
    gap: 7px;
  }
}

.lp-nav-active {
  padding-left: 14px;
}

/* ══════════════════════════════
   RIGHT PANEL
══════════════════════════════ */
.lp-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  min-width: 0;
}

/* Topbar */
.lp-topbar {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
  transition: background 0.3s ease;
}

@media (max-width: 600px) {
  .lp-topbar {
    height: 50px;
    padding: 0 12px;
  }
}

.lp-topbar-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}

@media (max-width: 600px) {
  .lp-topbar-title {
    font-size: 13px;
  }
}

.lp-topbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.lp-topbar-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

@media (max-width: 600px) {
  .lp-topbar-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
}

.lp-bell-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ef4444;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
}

.lp-topbar-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  transition: background 0.3s ease;
}

@media (max-width: 600px) {
  .lp-topbar-avatar {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
}

/* Page body */
.lp-page-body {
  flex: none;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (max-width: 600px) {
  .lp-page-body {
    padding: 12px;
    gap: 10px;
  }
}

/* Page header row */
.lp-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .lp-page-header {
    gap: 8px;
  }
}

.lp-page-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.lp-page-sub {
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
}

@media (max-width: 600px) {
  .lp-page-title {
    font-size: 14px;
  }

  .lp-page-sub {
    font-size: 10px;
  }
}

.lp-page-actions {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .lp-page-actions {
    gap: 5px;
  }
}

.lp-search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 5px 10px;
  min-width: 110px;
}

.lp-search-placeholder {
  font-size: 11px;
  color: #9ca3af;
}

@media (max-width: 600px) {
  .lp-search-box {
    min-width: 90px;
    padding: 4px 8px;
    border-radius: 6px;
  }

  .lp-search-placeholder {
    font-size: 10px;
  }
}

.lp-filter-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 5px 9px;
  font-size: 11px;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .lp-filter-btn {
    font-size: 10px;
    padding: 4px 7px;
    border-radius: 6px;
  }
}

.lp-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.lp-add-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  transition: background 0.3s ease;
}

@media (max-width: 600px) {
  .lp-icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }

  .lp-add-btn {
    font-size: 10px;
    padding: 4px 10px;
    border-radius: 6px;
  }
}

/* Table card */
.lp-table-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  flex: none;
  display: flex;
  flex-direction: column;
}

/* Table head */
.lp-table-head {
  display: grid;
  grid-template-columns: 52px 1.8fr 1.2fr 1.5fr 1.2fr 110px;
  padding: 8px 14px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.lp-th {
  font-size: 9.5px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}

@media (max-width: 600px) {
  .lp-th {
    font-size: 9px;
    letter-spacing: 0.05em;
  }
}

/* Table rows */
.lp-table-row {
  display: grid;
  grid-template-columns: 52px 1.8fr 1.2fr 1.5fr 1.2fr 110px;
  padding: 9px 14px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
  transition: background 0.15s;
}

.lp-table-row:last-of-type {
  border-bottom: none;
}

.lp-table-row:hover {
  background: #f9fafb;
}

.lp-td {
  font-size: 11px;
  color: #374151;
  display: flex;
  align-items: center;
}

@media (max-width: 600px) {
  .lp-td {
    font-size: 10px;
  }
}

.lp-col-no {
  justify-content: center;
  color: #9ca3af;
  font-size: 10px;
}

.lp-col-name {
  gap: 8px;
}

.lp-col-house {}

.lp-col-contact {
  gap: 2px;
}

.lp-col-active {}

.lp-col-actions {
  gap: 2px;
}

.lp-member-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  transition: background 0.3s ease, color 0.3s ease;
}

.lp-member-name {
  font-size: 11px;
  font-weight: 600;
  color: #111827;
  line-height: 1.2;
}

.lp-no-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 4px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  background: #f1f5f9;
}

.lp-house-badge {
  display: inline-flex;
  align-items: center;
  gap: 3.5px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 6px;
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
}

.lp-action-group {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

.lp-action-btn {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.lp-action-delete {
  border-color: #fca5a5;
}

/* Pagination */
.lp-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-top: 1px solid #f3f4f6;
  background: #fafafa;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .lp-pagination {
    padding: 6px 10px;
    gap: 6px;
    flex-wrap: wrap;
  }
}

.lp-show-rows {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  color: #6b7280;
}

.lp-rows-select {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 600;
  color: #111827;
}

@media (max-width: 600px) {
  .lp-show-rows {
    font-size: 9px;
    gap: 6px;
  }

  .lp-rows-select {
    font-size: 9px;
    padding: 1px 5px;
  }
}

.lp-page-btns {
  display: flex;
  align-items: center;
  gap: 4px;
}

.lp-page-info {
  font-size: 10px;
  color: #6b7280;
  margin-right: 6px;
}

.lp-page-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  border: 1px solid #e5e7eb;
  background: #fff;
}

@media (max-width: 600px) {
  .lp-page-info {
    font-size: 9px;
    margin-right: 4px;
  }

  .lp-page-btn {
    width: 22px;
    height: 22px;
    font-size: 9px;
  }
}

.lp-page-btn-disabled {
  color: #9ca3af;
}

.lp-page-btn-active {
  color: #fff;
  border-color: transparent;
  transition: background 0.3s ease;
}

/* ── Shimmer skeleton ─────────────────── */
.lp-shimmer {
  background-size: 200% 100%;
  animation: lpShimmer 1.4s ease-in-out infinite;
  display: inline-block;
}

@keyframes lpShimmer {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}

/* Find your active nav item style — probably something like: */
.nav-item.active,
.nav-item--active {
  background: var(--brand, primaryColor);  /* ← this is the theme color */
  color: var(--primaryColor);
}

/* Add dark mode override: */
.theme--dark .nav-item.active,
.pt-dark .nav-item.active {
  background: rgba(255, 255, 255, 0.10);   /* subtle white tint */
  color: #ffffff !important;
}

.theme--dark .nav-item.active .v-icon,
.pt-dark .nav-item.active .v-icon {
  color: #ffffff !important;
}
</style>
